import React from 'react';
import useSetActivePage from '@hooks/useSetActivePage';
import { PageView } from '@store/session/types';
import { Box } from '@mui/material';
import { Helmet } from 'react-helmet';
import { NavBar } from '@components/common/NavBar/NavBar';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import { HealthTabs, OverviewTabs } from '@store/views/overviewView/types';
import { useSearchParams } from 'react-router-dom';
import Loading from '@components/common/Loading';
import useRenderOnWindowResize from '@hooks/useRenderOnWindowResize';
import { selectIsSuperAdminUser } from '@store/selectors';
import HealthContent from '@components/health/HealthContent';
import useUpdateStreams from '@hooks/useUpdateStreams';
import useUpdateSites from '@hooks/useUpdateSites';

const TAB_PARAM = "tab";

const HealthView: React.FC<{}> = () => {
  useSetActivePage(PageView.Health);
  useRenderOnWindowResize();

  const [searchParams, setSearchParams] = useSearchParams();
  const [tabAsserted, setTabAsserted] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<"edge_devices" | "cameras">("cameras")

  const selectedCompany = useSelector((store: IStoreState) => store.selectedCompanies.selectedCompany);
  const isSuperAdministrator = useSelector(selectIsSuperAdminUser);
  const userData = useSelector((store: IStoreState) => store.session.userData);

  const loadingSites = useUpdateSites(selectedCompany?.companyId, [])
  const loadingStreams = useUpdateStreams(undefined, selectedCompany?.companyId, userData?.roles, userData?.userId);

  const loading = React.useMemo(() => {
    return Boolean(loadingSites === true && loadingStreams === true)
  }, [loadingSites, loadingStreams])

  const changeSearchParams = React.useCallback((key: string, value: string) => {
    searchParams.set(key, value)
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleNavbarOptionSelection = React.useCallback((tab: string) => {
    if (searchParams.get(TAB_PARAM) !== tab) {
      changeSearchParams(TAB_PARAM, tab)
    }
  }, [changeSearchParams, searchParams])

  const isValidTab = React.useCallback((tab: string) => {
    return tab === "edge_devices" ||
    tab === "cameras";
  }, [])

  const processActiveTab = React.useCallback(() => {
    const tab = searchParams.get(TAB_PARAM);
    if (tab && isValidTab(tab)) {
      setActiveTab(tab as HealthTabs)
    }

    setTabAsserted(true);
  }, [searchParams, isValidTab])

  const tabs = React.useMemo(() => {
    const sudoTabs = [];

    if (isSuperAdministrator) {
      sudoTabs.push(...[
        {
          value: OverviewTabs.Cameras.toString(),
          onClick: () => { handleNavbarOptionSelection("cameras") },
          label: "Cameras",
          dataCy: "overview-cameras-tab"
        },
        {
          value: OverviewTabs.EdgeDevices.toString(),
          onClick: () => { handleNavbarOptionSelection("edge_devices") },
          label: "Edge devices",
          dataCy: "overview-edge-devices-tab"
        }
      ])
    }

    return [...sudoTabs]
  }, [handleNavbarOptionSelection, isSuperAdministrator])

  React.useEffect(processActiveTab, [processActiveTab])

  if (!tabAsserted) return null;

  return (
    <Box width="100%" height="100%" display="grid" gridTemplateRows="min-content auto">
      <Helmet title="Forsight | Health" />
      <NavBar
        title="Health"
        selectedValue={activeTab.toString()}
        tabs={tabs}
      />
      {loading === true &&
        <Loading />
      }
      {loading === false &&
        <HealthContent activeTab={activeTab as HealthTabs} />
      }
    </Box>
  )
}


export default HealthView;
